import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import type { Observable } from 'rxjs';
import type { BusinessStatusEnumKey } from './generated/models/BusinessStatus';
import { BaseApiService } from './base-api.service';

export type CreateWorkspaceRequest = {
  name: string;
};

export type CreateWorkspaceResponse = {
  business: {
    id: string;
    name: string;
    status: BusinessStatusEnumKey;
  };
  membership: {
    id: string;
    businessId: string;
    status: string;
  };
};

export type SetupRole = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  status: string;
  permissionKeys?: string[];
};

export type CreateInvitationRequest = {
  businessId: string;
  email: string;
  roleIds: string[];
  teams?: Array<{ teamId: string; teamRole: 'lead' | 'member' }>;
  expiresInDays?: number;
};

export type InvitationSetupResponse = {
  id: string;
  businessId: string;
  email: string;
  status: string;
  expiresAt: string;
  token?: string;
  createdAt: string;
  updatedAt: string;
};

export type ListInvitationsResponse = {
  invitations: InvitationSetupResponse[];
};

export type JoinWorkspaceRequest = {
  businessId: string;
};

export type JoinWorkspaceResponse = {
  business: {
    id: string;
    name: string;
    status: BusinessStatusEnumKey;
  };
  invitation: {
    id: string;
    businessId: string;
    email: string;
    status: string;
    expiresAt: string;
    token?: string;
  };
};

export type AcceptInvitationRequest = {
  token: string;
};

export type InvitationPreviewResponse = {
  email: string;
  businessName: string;
  hasAccount: boolean;
  expiresAt: string;
};

export type AcceptInvitationResponse = {
  invitation: {
    id: string;
    businessId: string;
    email: string;
    status: string;
    expiresAt: string;
    createdAt: string;
    updatedAt: string;
  };
  membership: {
    id: string;
    businessId: string;
    userId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
};

export type CreateEmailDomainRequest = {
  businessId: string;
  domain: string;
  autoInviteEnabled: boolean;
  includeSubdomains?: boolean;
  defaultExpiresInDays?: number;
  roleIds?: string[];
  teams?: Array<{ teamId: string; teamRole: 'lead' | 'member' }>;
};

export type EmailDomainSetupResponse = {
  id: string;
  businessId: string;
  domain: string;
  status: string;
  autoInviteEnabled: boolean;
  includeSubdomains: boolean;
  defaultExpiresInDays: number;
  verifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

@Injectable({ providedIn: 'root' })
export class CoreOnboardingApiService extends BaseApiService<CreateWorkspaceResponse> {
  protected override readonly resourcePath = '/v1/core/onboarding';

  createWorkspace(body: CreateWorkspaceRequest): Observable<CreateWorkspaceResponse> {
    return this.post<CreateWorkspaceResponse, CreateWorkspaceRequest>('create-workspace', body);
  }

  /** Domain-matched auto-invite candidate from the login/email-code discovery response. Creates
   * a PENDING invitation — does not grant access by itself, see acceptInvitation. */
  joinWorkspace(body: JoinWorkspaceRequest): Observable<JoinWorkspaceResponse> {
    return this.post<JoinWorkspaceResponse, JoinWorkspaceRequest>('join', body);
  }

  acceptInvitation(body: AcceptInvitationRequest): Observable<AcceptInvitationResponse> {
    return this.post<AcceptInvitationResponse, AcceptInvitationRequest>('/v1/core/invitations/accept', body);
  }

  /** Unauthenticated lookup — powers the /aceitar-convite landing page before any session
   * exists. Separate prefix (`/v1/invitations`, not `/v1/core/invitations`): the latter sits
   * behind a blanket requireAuth and can't serve an anonymous visitor. */
  previewInvitation(token: string): Observable<InvitationPreviewResponse> {
    return this.get<InvitationPreviewResponse>('/v1/invitations/preview', { params: { token } });
  }

  listRoles(businessId: string): Observable<SetupRole[]> {
    return this.get<{ roles: SetupRole[] }>('/v1/core/roles', {
      params: { businessId },
    }).pipe(map((response) => response.roles.filter((role) => role.status === 'active' && role.key !== 'owner')));
  }

  listInvitations(businessId: string): Observable<InvitationSetupResponse[]> {
    return this.get<ListInvitationsResponse>('/v1/core/invitations', {
      params: { businessId },
    }).pipe(map((response) => response.invitations));
  }

  createInvitation(body: CreateInvitationRequest): Observable<InvitationSetupResponse> {
    return this.post<InvitationSetupResponse, CreateInvitationRequest>('/v1/core/invitations', body);
  }

  revokeInvitation(invitationId: string): Observable<InvitationSetupResponse> {
    return this.post<InvitationSetupResponse, Record<string, never>>(
      `/v1/core/invitations/${invitationId}/revoke`,
      {},
    );
  }

  createEmailDomain(body: CreateEmailDomainRequest): Observable<EmailDomainSetupResponse> {
    return this.post<EmailDomainSetupResponse, CreateEmailDomainRequest>('/v1/core/email-domains', body);
  }
}
